import api from './api';
import {
  Rechnung, UpdateRechnungRequest, CreateRechnungRequest, CreateRechnungspositionRequest,
  RechnungslaufErgebnis, AbrechnungHistorieEintrag, Rechnungsvorschau,
  Rechnungseinstellungen, UpdateRechnungseinstellungenRequest,
} from '../types';

export interface RechnungQuery {
  status?: string;
  kundeId?: number;
  jahr?: number;
}

export const rechnungService = {
  getAll: async (params?: RechnungQuery): Promise<Rechnung[]> => {
    const response = await api.get('/rechnungen', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Rechnung> => {
    const response = await api.get(`/rechnungen/${id}`);
    return response.data;
  },

  // Schnellrechnung von Hand (optional gleich gestellt).
  create: async (data: CreateRechnungRequest): Promise<Rechnung> => {
    const response = await api.post('/rechnungen', data);
    return response.data;
  },

  // Nur im Entwurf erlaubt – danach antwortet das Backend mit 400.
  update: async (id: number, data: UpdateRechnungRequest): Promise<Rechnung> => {
    const response = await api.put(`/rechnungen/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/rechnungen/${id}`);
  },

  addPosition: async (id: number, data: CreateRechnungspositionRequest): Promise<Rechnung> => {
    const response = await api.post(`/rechnungen/${id}/positionen`, data);
    return response.data;
  },

  deletePosition: async (id: number, positionId: number): Promise<Rechnung> => {
    const response = await api.delete(`/rechnungen/${id}/positionen/${positionId}`);
    return response.data;
  },

  stellen: async (id: number): Promise<Rechnung> => {
    const response = await api.post(`/rechnungen/${id}/stellen`);
    return response.data;
  },

  bezahlt: async (id: number): Promise<Rechnung> => {
    const response = await api.post(`/rechnungen/${id}/bezahlt`);
    return response.data;
  },

  stornieren: async (id: number): Promise<Rechnung> => {
    const response = await api.post(`/rechnungen/${id}/stornieren`);
    return response.data;
  },

  sofortrechnung: async (vertragId: number, jahr?: number): Promise<Rechnung> => {
    const response = await api.post('/rechnungen/sofort', null, { params: { vertragId, jahr } });
    return response.data;
  },

  // Was der tägliche Lauf von morgen bis `bis` (ISO, Standard Monatsende) anlegen wird – nur gerechnet.
  vorschau: async (params?: { bis?: string; kundeId?: number }): Promise<Rechnungsvorschau> => {
    const response = await api.get('/rechnungen/vorschau', { params });
    return response.data;
  },

  // ADMIN: stößt den täglichen Lauf von Hand an – je fälligem Vertrag (Jahrestag erreicht) ein Entwurf.
  lauf: async (): Promise<RechnungslaufErgebnis> => {
    const response = await api.post('/rechnungen/lauf');
    return response.data;
  },

  getHistorie: async (vertragId: number): Promise<AbrechnungHistorieEintrag[]> => {
    const response = await api.get(`/vertraege/${vertragId}/abrechnung`);
    return response.data;
  },

  /**
   * Lädt das gespeicherte PDF herunter. Wie beim Excel-Export über axios + Object-URL, weil ein
   * einfacher Link den JWT nicht mitschicken würde.
   */
  downloadPdf: async (id: number, rechnungsnummer?: string | null): Promise<void> => {
    const response = await api.get(`/rechnungen/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `Rechnung-${rechnungsnummer || id}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const rechnungseinstellungenService = {
  get: async (): Promise<Rechnungseinstellungen> => {
    const response = await api.get('/rechnungseinstellungen');
    return response.data;
  },
  update: async (data: UpdateRechnungseinstellungenRequest): Promise<Rechnungseinstellungen> => {
    const response = await api.put('/rechnungseinstellungen', data);
    return response.data;
  },
};

export default rechnungService;
